import { SelectOption, useVacanciesOptionsApi } from '@innowise-group/core';
import { Checkbox, Select, SelectProps } from '@innowise-group/mui-kit';
import React, { useMemo } from 'react';
import * as Styled from './vacancy-select.styles';
import { VacancyListItem } from './components/vacancy-list-item';

type VacancySelectProps = Omit<
  SelectProps,
  'options' | 'loading' | 'loadingAdditionalOptions' | 'lastElementRef' | 'onBlur'
> & {
  filterStrategy?: (option: SelectOption) => boolean;
};

const VacancySelect: React.FC<VacancySelectProps> = ({ multiple, value, filterStrategy, ...props }) => {
  const {
    options: vacanciesOptions,
    items: vacanciesItems,
    resetEmptyState,
    lastElementRef,
    optionsLoading,
    additionalOptionsLoading,
  } = useVacanciesOptionsApi();

  const vacanciesOptionList = vacanciesOptions || [];

  const filteredOptionList = useMemo(() => {
    if (typeof filterStrategy === 'function') {
      const filteredOptions = vacanciesOptionList.filter((option) => filterStrategy(option));

      // { value: '-1', title: '' } is plug, don't delete it!!!
      // It's needed for cases where IntersectionObserver isn't called
      // because option-list is filtered on client side and became empty.
      // Consequently, pageNum counter isn't incremented and this
      // preventing a server request for new data.
      return filteredOptions.length > 0 ? filteredOptions : [{ value: '-1', title: '' }];
    }

    return vacanciesOptionList;
  }, [vacanciesOptionList, filterStrategy]);

  const renderVacancyListItem = (props, option: SelectOption) => {
    if (option.value === '-1') return <li key={option.value} ref={lastElementRef} />;

    const vacancy = vacanciesItems[option.value];

    if (!vacancy) return null;

    return multiple && Array.isArray(value) ? (
      <Styled.CheckboxOptionItem ref={lastElementRef} {...props} key={option.value}>
        <VacancyListItem {...vacancy} />
        <Styled.CheckboxWrapper>
          <Checkbox checked={!!value.find((item) => item.value === option.value)} />
        </Styled.CheckboxWrapper>
      </Styled.CheckboxOptionItem>
    ) : (
      <Styled.OptionItem
        {...props}
        ref={lastElementRef}
        key={option.value}
        selected={option.value === value}
        autoFocus={option.value === value}
      >
        <VacancyListItem {...vacancy} />
      </Styled.OptionItem>
    );
  };

  return (
    <Select
      {...props}
      value={value}
      multiple={multiple}
      options={filteredOptionList}
      loading={optionsLoading}
      onBlur={resetEmptyState}
      loadingAdditionalOptions={additionalOptionsLoading}
      renderOption={renderVacancyListItem}
    />
  );
};

export default VacancySelect;
